import { unique } from "@redotech/util/array";
import { escapeRegExp } from "@redotech/util/string";
import * as z from "zod/v4";

export const BrandAliasSchema = z.object({
  id: z.bigint(),
  alias: z.string(),
  regexMatch: z.string(),
});
export type BrandAlias = z.infer<typeof BrandAliasSchema>;

export function countBrandMentionsWithAliases({
  textToScan,
  brandName,
  regexMatches,
}: {
  textToScan: string;
  brandName: string;
  regexMatches: string[];
}): { mentionCount: number; brandMatches: BrandMatch[] } {
  const matcher = buildBrandMentionsMatcher(brandName, regexMatches);
  const { count, matches } = matcher.match(textToScan);

  return { mentionCount: count, brandMatches: matches };
}

function safeRegex(pattern: string): string {
  try {
    new RegExp(pattern);
    return pattern;
  } catch {
    return escapeRegExp(pattern);
  }
}

function ensureWordBoundaries(pattern: string): string {
  const trimmed = pattern.trim();
  const start = trimmed.startsWith("\\b") ? trimmed : `\\b${trimmed}`;
  const end = start.endsWith("\\b") ? start : `${start}\\b`;
  return end;
}

interface BrandMatch {
  matchText: string;
  start: number;
  end: number;
}

function buildBrandMentionsMatcher(brandName: string, regexPatterns: string[]) {
  const patterns: string[] = [];

  const safeRegexPatterns = unique([
    escapeRegExp(brandName),
    ...regexPatterns.map(safeRegex),
  ]);
  for (const pattern of safeRegexPatterns) {
    if (/\(/.test(pattern)) {
      patterns.push(ensureWordBoundaries(pattern));
    } else {
      patterns.push(
        ...pattern.split("|").map((b) => ensureWordBoundaries(b.trim())),
      );
    }
  }
  const uniquePatterns = unique(patterns);
  uniquePatterns.sort((a, b) => b.length - a.length);

  const combined = new RegExp(uniquePatterns.join("|"), "g");

  return {
    match(text: string): { count: number; matches: BrandMatch[] } {
      combined.lastIndex = 0;
      const raw: BrandMatch[] = [];
      let m: RegExpExecArray | null;

      while ((m = combined.exec(text)) !== null) {
        raw.push({
          matchText: m[0],
          start: m.index,
          end: m.index + m[0].length,
        });
        if (m[0].length === 0) combined.lastIndex++;
      }

      raw.sort(
        (a, b) => b.end - b.start - (a.end - a.start) || a.start - b.start,
      );
      const kept: BrandMatch[] = [];
      for (const c of raw) {
        if (!kept.some((k) => c.start < k.end && c.end > k.start)) {
          kept.push(c);
        }
      }
      kept.sort((a, b) => a.start - b.start);

      return { count: kept.length, matches: kept };
    },
  };
}
